import React, { useEffect, useState } from 'react';
import { HashLink } from 'react-router-hash-link';
import Particles from 'react-tsparticles';
import { loadFull } from 'tsparticles';
import '../css/styles.css';

const sections = [
  {
    id: "services",
    eyebrow: "Services",
    title: "What we build for you",
    lead: "Custom AI and web solutions, designed around how your business actually works.",
    items: [
      { image: `${process.env.PUBLIC_URL}/images/AI_Agent.png`, alt: "AI Agents", link: "/services#aiagent", titleText: "AI Agents", description: "AI agents are revolutionizing industries by automating complex tasks, generating actionable insights and enabling new services. At Manvidia, we develop bespoke AI agents tailored to your specific challenges, so you can make the most of your potential." },
      { image: `${process.env.PUBLIC_URL}/images/web_development.png`, alt: "Web Development", link: "/services#webdev", titleText: "Website Designing", description: "A responsive, modern website built for your business. We handle everything from the first concept and design through development and deployment, giving you an online presence that reflects your brand and resonates with your audience." }
    ]
  },
  {
    id: "products",
    eyebrow: "Products",
    title: "Software we build and run",
    lead: "Ready-to-use AI tools that help online sellers understand customers and track their money.",
    items: [
      { image: `${process.env.PUBLIC_URL}/images/Sentiment.png`, alt: "Comment IQ", link: "/products#commentIQ", titleText: "Comment IQ", contain: true, description: "Understand your audience better. Comment IQ analyzes the comments on your Instagram and Facebook posts, separating positive, negative and request-based feedback, so you can respond to customers faster and tailor your content for maximum impact." },
      { image: `${process.env.PUBLIC_URL}/images/ReconIQ.svg`, alt: "ReconIQ payment reconciliation", link: "/products#reconIQ", titleText: "ReconIQ", contain: true, description: "Make sure the marketplace pays you every rupee you're owed. ReconIQ reconciles each order against your settlement reports, accounting for fees, taxes, refunds and ad spend, and flags missing or short payouts. Works with Amazon today, with Flipkart support coming soon." }
    ]
  }
];

const particleOptions = (count) => ({
  fullScreen: { enable: false },
  detectRetina: true,
  fpsLimit: 60,
  background: { color: { value: 'transparent' } },
  particles: {
    number: { value: count },
    color: { value: '#b9a8ff' },
    opacity: { value: 0.6 },
    shape: { type: 'circle' },
    size: { value: 2 },
    move: { enable: true, speed: 0.6 },
    links: { enable: true, color: '#b9a8ff', opacity: 0.25, distance: 150 },
  },
  interactivity: {
    events: { onHover: { enable: true, mode: 'grab' } },
    modes: { grab: { distance: 160, links: { opacity: 0.5 } } },
  },
});

const prefersReducedMotion = () =>
  window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const Home = () => {
  const [particleCount, setParticleCount] = useState(
    window.innerWidth <= 768 ? 25 : 70
  );

  useEffect(() => {
    const updateParticleCount = () => {
      setParticleCount(window.innerWidth <= 768 ? 25 : 70);
    };
    window.addEventListener('resize', updateParticleCount);
    return () => window.removeEventListener('resize', updateParticleCount);
  }, []);

  const particlesInit = async (engine) => {
    await loadFull(engine);
  };

  return (
    <div>
      {/* Hero Section */}
      <section className="hero">
        {!prefersReducedMotion() && (
          <Particles
            id="tsparticles"
            className="hero-particles"
            init={particlesInit}
            options={particleOptions(particleCount)}
          />
        )}
        <div className="page">
          <div className="hero-content">
            <span className="eyebrow">AI solutions · Products · Services</span>
            <h1>Welcome to MANVIDIA</h1>
            <p className="hero-lead">Your partner in innovative AI solutions. We design AI agents, build websites and run software products that help businesses grow.</p>
            <div className="hero-actions">
              <HashLink smooth to="#services" className="btn">Explore our services</HashLink>
              <HashLink smooth to="#products" className="btn btn-ghost">See our products</HashLink>
            </div>
          </div>
        </div>
      </section>

      {/* Services and Products */}
      {sections.map(section => (
        <section className="section" id={section.id} key={section.id}>
          <div className="page">
            <div className="section-heading" data-aos="fade-up">
              <span className="eyebrow">{section.eyebrow}</span>
              <h2 className="section-title">{section.title}</h2>
              <p>{section.lead}</p>
            </div>
            <div className="card-grid">
              {section.items.map((item, index) => (
                <HashLink smooth to={item.link} className="card" key={item.titleText} data-aos="fade-up" data-aos-delay={index * 100}>
                  <div className={`card-media ${item.contain ? 'card-media--contain' : ''}`}>
                    <img src={item.image} alt={item.alt} />
                  </div>
                  <div className="card-body">
                    <h3>{item.titleText}</h3>
                    <p>{item.description}</p>
                    <span className="text-link">Learn more <span aria-hidden="true">→</span></span>
                  </div>
                </HashLink>
              ))}
            </div>
          </div>
        </section>
      ))}

      {/* Opportunities Section */}
      <section className="section" id="opportunities">
        <div className="page">
          <div className="section-heading" data-aos="fade-up">
            <span className="eyebrow">Opportunities</span>
            <h2 className="section-title">Grow your career with us</h2>
          </div>
          <div className="banner" data-aos="fade-up">
            <img src={`${process.env.PUBLIC_URL}/images/Internship.png`} alt="Internship" />
            <div>
              <h3>Internships</h3>
              <p>Launch your career in tech with Manvidia. Our internship program gives you hands-on experience in our core areas: AI, data science and full-stack development. Work on real-world projects, collaborate with our experts and build a portfolio that showcases your talent.</p>
              <HashLink to="/opportunities" className="btn">Learn more</HashLink>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
